import {
  createRouter,
  createWebHashHistory,
  NavigationFailure,
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteRecordRaw,
} from 'vue-router'
import { Store } from 'pinia'

import MainFrame from '@/popup/components/frames/Main/MainFrame.vue'
import EntryFrame from '@/popup/components/frames/Entry/EntryFrame.vue'
import entryRoutes from '@/popup/components/frames/Entry/entryRoutes'
import OnboardFrame from '@/popup/components/frames/OnboardFrame/OnboardFrame.vue'
import ImportFrame from '@/popup/components/frames/ImportFrame/ImportFrame.vue'
import RestoreFrame from '@/popup/components/frames/RestoreFrame/RestoreFrame.vue'
import NotFound from '@/popup/components/views/NotFound/NotFound.vue'
import onboardRoutes from '@/popup/components/frames/OnboardFrame/onboardRoutes'
import mainRoutes from '@/popup/components/frames/Main/mainRoutes'
import importRoutes from '@/popup/components/frames/ImportFrame/importRoutes'
import restoreRoutes from '@/popup/components/frames/RestoreFrame/restoreRoutes'
import { AppState, useAppStore } from '@/popup/store/AppStore'
import { saveLastState } from '@/popup/storage/Database'
import { useUIStore } from '@/popup/store/UIStore'
import { useApprovalsStore } from '@/popup/store/ApprovalsStore'
// import { Approval, ApprovalApi } from '@/shared/apis/approvals'

const ADD_ACCOUNT_ROUTE = '/add-account'
const CREATE_ADDRESS_ROUTE = '/create-address'

function checkExistingUser(
  appStore: Store<
    'app',
    AppState,
    {
      currentProfile: {
        address: string
      }
    }
  >
): boolean {
  return !!appStore.selectedProfile && appStore.currentProfile.address !== '0x'
}

function checkUnlocked(appStore: AppState): boolean {
  return appStore?.state?.isUnlocked
}

function checkCreatedUser(appStore: AppState): boolean {
  return appStore?.state?.isCreated
}

// const getApprovalPath = (approvals: Approval<keyof ApprovalApi, unknown>[]) => {
//   let approvalPath = ''
//   // const uiStore = useUIStore()
//   if (approvals.length > 0) {
//     const approval = approvals[0]
//     approvalPath = {
//       transaction: () => `/transaction/${approval.id}`,
//       sign: () => `/sign/${approval.id}`,
//       requestAccounts: () => `/web3-auth/${approval.id}`,
//       import: () => `/qrcode/import-profile-dapp/${approval.id}`,
//       relayer: () => `/relayer-auth/${approval.id}`,
//       switchNetwork: () => `/network-switch/${approval.id}`,
//       addNetwork: () => `/settings/network/${approval.id}`,
//       createAddress: () => `/create-address/${approval.id}`,
//     }[approval.type]()
//   }

//   return approvalPath
// }

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'root',
    redirect: () => {
      const appStore = useAppStore()
      const isUnlocked = checkUnlocked(appStore)
      console.log('Redirect called', appStore.currentProfile)
      let name = 'welcome'
      if (isUnlocked) {
        name = 'home'
      } else if (checkExistingUser(appStore)) {
        name = 'authenticate'
      } else if (checkCreatedUser(appStore)) {
        name = 'addAccount'
      }
      console.log('Redirect to', name)
      return { name }
    },
    component: EntryFrame,
    children: entryRoutes,
  },
  { path: '/onboard', component: OnboardFrame, children: onboardRoutes },
  { path: '/import', component: ImportFrame, children: importRoutes },
  { path: '/restore', component: RestoreFrame, children: restoreRoutes },
  { path: '/home', component: MainFrame, children: mainRoutes },
  { path: '/:pathMatch(.*)*', name: 'NotFound', component: NotFound },
]

function beforeEachRoute(
  to: RouteLocationNormalized,
  _: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const appStore = useAppStore()
  const uiStore = useUIStore()
  uiStore.updateHeight(to.path, appStore.state.isUnlocked, false)
  const approvalsStore = useApprovalsStore()
  const approval = approvalsStore.nonStashedApprovals.at(0)

  const isExistingUser = checkExistingUser(appStore)
  const isUserCreated = checkCreatedUser(appStore)
  const isUserUnlocked = checkUnlocked(appStore)

  const isAuthRoute = to.path.startsWith('/auth')
  const isSetupAccountRoute =
    to.path.startsWith('/add-base-account') ||
    to.path.startsWith(ADD_ACCOUNT_ROUTE) ||
    to.path.startsWith(CREATE_ADDRESS_ROUTE) ||
    to.path.startsWith('/qrcode') ||
    to.path.startsWith('/restore')
  const isOnboardRoute =
    to.path.startsWith('/create-account') ||
    to.path.startsWith('/welcome') ||
    to.path.startsWith('/welcome-info') ||
    to.path.startsWith('/terms-of-use')
  const isSwitchNetworkRoute = to.path.startsWith('/network-switch')
  const notAuthRouteNorOnboardRoute = !(isAuthRoute || isOnboardRoute)

  if (notAuthRouteNorOnboardRoute && isUserCreated && !isUserUnlocked) {
    return next('/auth')
  }

  if (!isOnboardRoute && !isExistingUser && !isUserCreated && !isUserUnlocked) {
    return next('/welcome-info')
  }

  if (isSwitchNetworkRoute && approval?.type === 'switchNetwork') {
    return next()
  }

  if (isUserUnlocked && approval?.type === 'createAddress') {
    return next()
  }

  if (
    isUserCreated &&
    !isExistingUser &&
    isUserUnlocked &&
    !isSetupAccountRoute
  ) {
    console.log(
      `Before each route navigates to ${ADD_ACCOUNT_ROUTE}`,
      isExistingUser,
      appStore.currentProfile,
      appStore.selectedProfile,
      appStore.profiles
    )
    return next(
      appStore.network.id.startsWith('base')
        ? '/add-base-account'
        : ADD_ACCOUNT_ROUTE
    )
  }

  // const approvalPath = getApprovalPath(approvalsStore.nonStashedApprovals)
  // if (approvalPath) {
  //   return next(approvalPath)
  // }

  next()
}

function afterEachRoute(
  to: RouteLocationNormalized,
  _: RouteLocationNormalized,
  failure: NavigationFailure | void
) {
  const blockedRoutes =
    to.path.startsWith('/create-account') ||
    to.path.startsWith(CREATE_ADDRESS_ROUTE) ||
    to.path.startsWith(ADD_ACCOUNT_ROUTE) ||
    to.path.startsWith('/welcome') ||
    to.path.startsWith('/terms-of-use') ||
    to.path.startsWith('/auth') ||
    to.path.startsWith('/web3-auth') ||
    to.path.startsWith('/transaction') ||
    to.path.startsWith('/network-switch') ||
    to.path.startsWith('/sign') ||
    to.path.startsWith('/qrcode') ||
    to.path.startsWith('/restore')

  const appStore = useAppStore()

  if (
    !failure &&
    !blockedRoutes &&
    useApprovalsStore().nonStashedApprovals.length === 0 &&
    checkExistingUser(appStore)
  ) {
    saveLastState({ path: to.fullPath })
  }
}

const router = createRouter({
  history: createWebHashHistory(),
  routes,
})

router.beforeEach(beforeEachRoute)
router.afterEach(afterEachRoute)
export default router
